'use client'

import {
  Button,
  InputText,
  Select,
  SelectItem,
  Typography,
} from '@dafiti/closet-ui'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { cn } from '@dafiti/closet-ui/utils'
import { NewsletterSubmitFeedback } from './NewsletterSubmitFeedback'
import type { NewsletterFormSchema } from '@/hooks/useNewsletterSubscription'
import { subscribeNewsletterMutationOptions } from '@/queries/newsletter'
import { useNewsletterSubscription } from '@/hooks/useNewsletterSubscription'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { couponRegistrationClick } from '@/tracking/footer'
import { useMessages } from '@/i18n/useMessages'

export function NewsletterForm() {
  const [showFeedback, setShowFeedback] = useState(false)

  const { validate, formStates, clearState, updateFormState } =
    useNewsletterSubscription()

  const { options } = useAppLayoutContext()
  const { apiGatewayBaseUrl, baseDomain } = options
  const t = useMessages()

  const emailRef = useRef<HTMLInputElement>(null)

  const subscribeNewsletter = useMutation({
    ...subscribeNewsletterMutationOptions({
      apiOrigin: apiGatewayBaseUrl,
      store: options.store || 'dafiti-br',
    }),
    onError: (error: Error) => {
      const errorCode = error.message

      if (errorCode === 'email_already_registered') {
        setShowFeedback(true)
        return
      }

      const errorMessages: Record<
        string,
        { state: 'support' | 'danger'; helperText: string }
      > = {
        invalid_email: {
          state: 'danger',
          helperText: t.newsletter.invalidEmail,
        },
      }

      const errorConfig = errorMessages[errorCode] || {
        state: 'danger',
        helperText: t.newsletter.genericError,
      }

      updateFormState('email', errorConfig)
    },
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const data = Object.fromEntries(
      new FormData(event.currentTarget),
    ) as NewsletterFormSchema

    const validationResult = validate(data)

    if (!validationResult.success) {
      if (!validationResult.error.formErrors.fieldErrors.gender) {
        emailRef.current?.focus()
      }

      return
    }

    couponRegistrationClick()

    subscribeNewsletter.mutate({
      email: data.email,
      gender: data.gender,
      source: 'footer',
    })
  }

  return (
    <section
      className={cn(
        'gap-3xs lg:gap-2xs xl:gap-sm relative flex min-h-[115px] flex-col items-center lg:flex-row lg:px-0',
      )}
      data-testid="newsletter-form"
    >
      <div className="lg:w-[250px]">
        <h4 id="newsletter-title" className="text-center lg:text-left">
          <Typography
            variant="subtitle-1"
            as="span"
            className="leading-tiny font-bold text-black"
          >
            {t.newsletter.title}
          </Typography>
          <br />
          <Typography
            variant="subtitle-2"
            as="span"
            className="leading-tiny font-medium text-black"
          >
            {t.newsletter.subtitle}
          </Typography>
        </h4>

        <Typography
          variant="body-3"
          as="p"
          id="newsletter-description"
          className="mt-nano text-center font-normal text-gray-500 lg:text-left"
        >
          {t.newsletter.description}{' '}
          {t.newsletter.rulesLinkUrl && t.newsletter.rulesLinkText && (
            <a
              className="font-bold underline"
              href={`${baseDomain}${t.newsletter.rulesLinkUrl}`}
            >
              {t.newsletter.rulesLinkText}
            </a>
          )}
        </Typography>
      </div>

      <form
        className={
          'gap-3xs xl:gap-2xs flex w-full flex-grow flex-col lg:max-h-[64px] lg:w-auto lg:flex-row'
        }
        action="#"
        onSubmit={handleSubmit}
        aria-labelledby="newsletter-title"
        aria-describedby="newsletter-description"
      >
        <div className="flex-grow basis-0">
          <Select
            label={t.newsletter.categoryLabel}
            className="w-full"
            name="gender"
            aria-label={t.newsletter.categoryAriaLabel}
            state={formStates.gender?.state}
            helperText={formStates.gender?.helperText}
            disabled={subscribeNewsletter.isPending}
            onValueChange={() => clearState('gender')}
          >
            <SelectItem value="male">{t.newsletter.genderMale}</SelectItem>
            <SelectItem value="female">{t.newsletter.genderFemale}</SelectItem>
          </Select>
        </div>

        <div className="flex-grow basis-0">
          <InputText
            name="email"
            size="lg"
            className="w-full"
            label={t.newsletter.emailLabel}
            aria-label={t.newsletter.emailAriaLabel}
            state={formStates.email?.state}
            helperText={formStates.email?.helperText}
            onChange={() => clearState('email')}
            ref={emailRef}
            disabled={subscribeNewsletter.isPending}
          />
        </div>

        <Button
          type="submit"
          className="min-h-[56px] w-full lg:w-[164px] 2xl:w-[236px]"
          size="lg"
          disabled={subscribeNewsletter.isPending}
        >
          {subscribeNewsletter.isPending
            ? t.newsletter.submitting
            : t.newsletter.submit}
        </Button>
      </form>

      {(subscribeNewsletter.isSuccess || showFeedback) && (
        <NewsletterSubmitFeedback />
      )}
    </section>
  )
}
